import { useState, useCallback, useEffect } from 'react';
import { Response, ExternalAccount, Pagination, PermissionEnum } from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetTransactionsExternalAccountsListQuery } from '../store/services';

interface ListResponse {
  data: Response<ExternalAccount>;
}

let timeout: NodeJS.Timeout;

const useGetExternalAccountsList = () => {
  const [externalAccountsPagination, setExternalAccountsPagination] = useState<
    Pagination | undefined
  >(undefined);
  const [externalAccountsList, setExternalAccountsList] = useState<
    ExternalAccount[]
  >([]);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);

  const {
    transactionsDictionaries: { activePermissions },
    incoming: {
      transaction: { externalAccounts, externalPagination },
    },
  } = useAppSelector((state) => state);

  const [getList, { isFetching, isLoading }] =
    useLazyGetTransactionsExternalAccountsListQuery();

  const getExternalAccountsListAction = useCallback(
    async ({
      search,
      cursor,
      searchKey,
    }: {
      search?: string;
      cursor?: string;
      searchKey?: string;
    }) => {
      let query = '?take=20';

      if (search) {
        if (!searchKey) {
          query = `${query}&iban=${search}`;
        } else if (searchKey) {
          query = `${query}&${searchKey}=${search}`;
        }

        if (cursor) {
          query = `${query}&cursor=${cursor}`;
        }
      } else if (!search && cursor) {
        query = `${query}&cursor=${cursor}`;
      }

      if (cursor) {
        setNextPageIsLoading(true);
      }

      const response = (await getList(query)) as ListResponse;

      if (response?.data?.data) {
        if (!cursor) {
          setExternalAccountsList(
            response?.data?.data?.edges?.map((item) => item.node),
          );
        } else if (cursor) {
          const nextList = ([] as ExternalAccount[]).concat(
            externalAccountsList,
          );

          response?.data?.data?.edges?.forEach((item) => {
            nextList.push(item.node);
          });

          setExternalAccountsList(nextList);
          setNextPageIsLoading(false);
        }

        setExternalAccountsPagination(response?.data?.data?.pageInfo);
      }
    },
    [externalAccountsList],
  );

  const onSearch = (search: string) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      getExternalAccountsListAction({ search });
    }, 300);
  };

  const onClear = () => {
    getExternalAccountsListAction({});
  };

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST) &&
      !externalAccountsList
    ) {
      if (!externalAccounts) {
        getExternalAccountsListAction({});
      } else {
        setExternalAccountsList(externalAccounts);
        setExternalAccountsPagination(externalPagination);
      }
    }
  }, [
    getExternalAccountsListAction,
    activePermissions,
    externalAccountsList,
    externalAccounts,
    externalPagination,
  ]);

  return {
    getExternalAccountsListAction,
    onSearch,
    isLoading: isLoading || isFetching,
    onClear,
    externalAccountsList,
    externalAccountsPagination,
    nextPageIsLoading,
    setExternalAccountsList,
    setExternalAccountsPagination,
  };
};

export default useGetExternalAccountsList;
